import { create } from 'zustand'
import type { CurrentUser, Portfolio, Project, AiMessage } from '@/types/ppm'
import type { BacklogFilters, SwimlaneBy, FilterPreset } from '@/types/agile'
import { DEFAULT_BACKLOG_FILTERS } from '@/types/agile'
import type { BoardItemLevel } from '@/components/agile/workItemTree'
import { getDemoProfile } from '@/types/demoProfiles'
import { demoCompanyCurrency } from '@/types/demoCompanies'
import { clearMockCache } from '@/services/dataService'
import { queryClient } from '@/lib/queryClient'
import { setCurrencyContext } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { mayLeaveOwnAccount } from '@/config/projectEntitlements'
import { toast } from 'sonner'
// Value import is safe: tesseractConfig only takes a *type* from this module,
// so there is no runtime cycle, and it reads import.meta.env at load time.
import { isContextEnabled } from '@/config/tesseractConfig'

export type ManagementContext = 'portfolio' | 'project' | 'contract' | 'organizational' | 'supplychain' | 'configuration' | 'timesheet' | 'mywork' | 'personnel' | 'workflow-management' | 'cost' | 'crm' | 'analytics' | 'it-ticketing'

export type AppMode = 'demo' | 'sandbox' | 'development' | 'quality' | 'production'

export interface AppModeConfig {
  label: string
  description: string
  useMockData: boolean
  allowConfigChanges: boolean
  allowConfigPromotion: boolean
  requiresAuth: boolean
  color: string
  bgColor: string
}

export const APP_MODE_CONFIGS: Record<AppMode, AppModeConfig> = {
  demo: {
    label: 'Demo',
    description: 'Pre-loaded demo data with read-only configuration',
    useMockData: true,
    allowConfigChanges: false,
    allowConfigPromotion: false,
    requiresAuth: false,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 border-blue-200',
  },
  sandbox: {
    label: 'Sandbox',
    description: 'User-generated data with full configuration access',
    useMockData: false,
    allowConfigChanges: true,
    allowConfigPromotion: false,
    requiresAuth: true,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50 border-amber-200',
  },
  development: {
    label: 'Development',
    description: 'Full access with configuration promotion to Quality',
    useMockData: false,
    allowConfigChanges: true,
    allowConfigPromotion: true,
    requiresAuth: true,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 border-blue-200',
  },
  quality: {
    label: 'Quality',
    description: 'Validate configuration before promoting to Production',
    useMockData: false,
    allowConfigChanges: false,
    allowConfigPromotion: true,
    requiresAuth: true,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50 border-emerald-200',
  },
  production: {
    label: 'Production',
    description: 'Live data — configuration changes are locked',
    useMockData: false,
    allowConfigChanges: false,
    allowConfigPromotion: false,
    requiresAuth: true,
    color: 'text-red-700',
    bgColor: 'bg-red-50 border-red-200',
  },
}

const DEMO_USER: CurrentUser = {
  id: 'demo_user',
  name: 'Demo User',
  role: 'GlobalViewer',
  organizationId: 'org_demo',
  // Real-auth users get their organization_name from auth metadata;
  // this default is demo-only.
  organizationName: 'Demo Organization',
  assignedPortfolios: ['port_001', 'port_002', 'port_003'],
  costCenterCode: '00-00-000',
  clearance: 'Secret',
  department: 'Demo',
  initials: 'DU',
}

// ──────────────────────────────────────────────────────────────────────────
// Selected-context persistence
//
// Without these, a hard refresh (F5) on any project / portfolio page
// resets selectedPortfolio + selectedProject to null. The route guard
// in App.tsx then bounces the user back to the Context Selector at "/".
// We persist the chosen portfolio + project + management context to
// localStorage on every selection so reloads stay where the user is.
// ──────────────────────────────────────────────────────────────────────────
const PERSIST_KEY_PORTFOLIO = 'ppm-selected-portfolio'
const PERSIST_KEY_PROJECT = 'ppm-selected-project'
const PERSIST_KEY_MGMT_CTX = 'ppm-management-context'
const PERSIST_KEY_TS_PAY_TYPE = 'ppm-timesheet-pay-type'
const PERSIST_KEY_TS_ROLE_OVERRIDE = 'ppm-timesheet-role-override'
/** Conversational Analytics: "Review plans before running" preference (5.3). */
const PERSIST_KEY_CA_REVIEW_PLANS = 'ppm-ca-review-plans'
/** Agile: saved backlog filter presets, keyed by project id. */
const PERSIST_KEY_BACKLOG_PRESETS = 'ppm-backlog-filter-presets'
const PERSIST_KEY_BOARD_ITEM_LEVEL = 'ppm-board-item-level'
/** Last-picked demo persona (PersonaSwitcher) — restored across refresh. */
export const PERSIST_KEY_DEMO_PERSONA = 'ppm-demo-persona'

export type TimesheetPayType = 'salary' | 'hourly'

/** Conversational Analytics plan-review gate: default off (auto-run, safe since
 *  every analytics tool is read-only). Persisted so the choice survives refresh. */
function loadPersistedCaReviewPlans(): boolean {
  try {
    return localStorage.getItem(PERSIST_KEY_CA_REVIEW_PLANS) === 'true'
  } catch {
    return false
  }
}

/** Saved backlog filter presets, keyed by project id. Persisted so a team's
 *  saved views survive a reload. Malformed storage falls back to empty. */
function loadPersistedBacklogPresets(): Record<string, FilterPreset[]> {
  try {
    const raw = localStorage.getItem(PERSIST_KEY_BACKLOG_PRESETS)
    if (!raw || raw[0] !== '{') return {}
    const parsed = JSON.parse(raw) as Record<string, FilterPreset[]>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function loadPersistedBoardItemLevel(): BoardItemLevel {
  try {
    return localStorage.getItem(PERSIST_KEY_BOARD_ITEM_LEVEL) === 'task' ? 'task' : 'story'
  } catch {
    return 'story'
  }
}

function persistBacklogPresets(presets: Record<string, FilterPreset[]>): void {
  try {
    localStorage.setItem(PERSIST_KEY_BACKLOG_PRESETS, JSON.stringify(presets))
  } catch {
    /* ignore */
  }
}

function loadPersistedTimesheetPayType(): TimesheetPayType {
  try {
    const raw = localStorage.getItem(PERSIST_KEY_TS_PAY_TYPE)
    if (raw === 'salary' || raw === 'hourly') return raw
  } catch {
    /* ignore */
  }
  return 'hourly'
}

/** Demo-only timesheet access-role override (PersonaSwitcher). Only hydrated
 * in demo mode so it can never leak into a real authenticated session. */
function loadPersistedRoleOverride(): string | null {
  try {
    if (loadPersistedMode() !== 'demo') return null
    return (
      localStorage.getItem(PERSIST_KEY_TS_ROLE_OVERRIDE) ??
      loadPersistedPersona()?.systemRole ??
      null
    )
  } catch {
    return null
  }
}

/** Self-contained demo persona snapshot (PersonaSwitcher). Persisting the full
 * identity — not just the employee id — lets us restore the active persona on a
 * refresh WITHOUT waiting for the async employee reference cache to warm
 * (getEmployee() is empty on first paint in supabase mode, which previously
 * dropped the persona back to the default Demo User). Demo-only. */
export interface DemoPersonaSnapshot {
  id: string
  name: string
  initials: string
  appRole: CurrentUser['role']
  systemRole: string
}

function loadPersistedPersona(): DemoPersonaSnapshot | null {
  try {
    if (loadPersistedMode() !== 'demo') return null
    const raw = localStorage.getItem(PERSIST_KEY_DEMO_PERSONA)
    if (!raw || raw[0] !== '{') return null // older builds stored a bare id string
    const p = JSON.parse(raw) as Partial<DemoPersonaSnapshot>
    if (!p.id || !p.name) return null
    return {
      id: p.id,
      name: p.name,
      initials: p.initials ?? '',
      appRole: (p.appRole ?? 'PortfolioManager') as CurrentUser['role'],
      systemRole: p.systemRole ?? 'Supervisor',
    }
  } catch {
    return null
  }
}

function loadPersistedPortfolio(): Portfolio | null {
  try {
    const raw = localStorage.getItem(PERSIST_KEY_PORTFOLIO)
    return raw ? (JSON.parse(raw) as Portfolio) : null
  } catch {
    return null
  }
}

function loadPersistedProject(): Project | null {
  try {
    const raw = localStorage.getItem(PERSIST_KEY_PROJECT)
    return raw ? (JSON.parse(raw) as Project) : null
  } catch {
    return null
  }
}

// Every context the app can actually render. The persisted value is a bare
// string written by an earlier build, so it MUST be validated before it is
// trusted: a stale key (the retired 'construction' placeholder, say) used to
// be cast straight through, leaving the shell in a context with no nav groups
// and no way back except clearing site data. Same guard as loadPersistedMode.
const KNOWN_MANAGEMENT_CONTEXTS: readonly ManagementContext[] = [
  'portfolio', 'project', 'contract', 'organizational', 'mywork', 'personnel',
  'configuration', 'supplychain', 'timesheet', 'workflow-management', 'cost',
  'crm', 'analytics', 'it-ticketing',
]

function loadPersistedManagementContext(): ManagementContext | null {
  try {
    const raw = localStorage.getItem(PERSIST_KEY_MGMT_CTX)
    if (!raw) return null
    // Unknown context (renamed or retired since the value was written), or one
    // whose owning module is disabled for this deployment: drop it and send the
    // user to the Context Selector rather than into a Studio that cannot render.
    if (!KNOWN_MANAGEMENT_CONTEXTS.includes(raw as ManagementContext)) return null
    if (!isContextEnabled(raw as ManagementContext)) return null
    return raw as ManagementContext
  } catch {
    return null
  }
}

function loadPersistedMode(): AppMode {
  try {
    const stored = localStorage.getItem('ppm-app-mode')
    if (stored && stored in APP_MODE_CONFIGS) return stored as AppMode
    // Persist the default so dataService can read it from localStorage
    localStorage.setItem('ppm-app-mode', 'demo')
  } catch { /* ignore */ }
  return 'demo'
}

function loadPersistedCompany(): string | null {
  try { return localStorage.getItem('ppm-demo-company') || null } catch { return null }
}

function loadPersistedSandboxOrg(): string | null {
  try { return localStorage.getItem('ppm-sandbox-org') || null } catch { return null }
}

// Display-currency override (null = follow the selected company's native currency).
// Lets the user flip the whole app between e.g. A$ and US$ (see lib/utils currency
// context). Persisted so an in-company choice survives a refresh — but SCOPED to
// the company it was set on ("<companyId>:<code>") so it can't bleed onto a
// different company on a fresh page load (the switch-reset only fires on an actual
// switch, not a refresh). A legacy bare/unscoped value is dropped, so an old
// stale USD can't leak onto e.g. Codan (native A$).
function loadPersistedDisplayCurrency(companyId: string | null): string | null {
  try {
    const raw = localStorage.getItem('ppm-display-currency')
    if (!raw) return null
    const sep = raw.indexOf(':')
    if (sep === -1) { localStorage.removeItem('ppm-display-currency'); return null } // legacy unscoped → drop
    const storedCompany = raw.slice(0, sep)
    const code = raw.slice(sep + 1)
    if (!code || storedCompany !== (companyId ?? '')) return null // belongs to another company → ignore
    return code
  } catch { return null }
}

// Push the (base = company native, display = override ?? native) pair into the
// lib/utils money formatters. Called on company change, override change, and init.
function syncCurrencyContext(companyId: string | null, override: string | null): void {
  const native = demoCompanyCurrency(companyId)
  setCurrencyContext(native, override ?? native)
}

// Active organization (production edition) — the over-arching scope that frames
// EVERY module. Persisted to the same `ppm-organization-id` key that
// lib/orgContext.ts + the auth bridge already read/write, so the global org seam
// stays single-sourced. Set on login (roster bind) and by the top Active-Org bar.
function loadPersistedActiveOrg(): string | null {
  try { return localStorage.getItem('ppm-organization-id') || null } catch { return null }
}

function loadPersistedDemoProfile(): string {
  try {
    const stored = localStorage.getItem('ppm-demo-profile')
    // Migrate legacy 'default' id to 'technology-digital'
    if (!stored || stored === 'default') {
      localStorage.setItem('ppm-demo-profile', 'technology-digital')
      return 'technology-digital'
    }
    return stored
  } catch { /* ignore */ }
  return 'technology-digital'
}

interface AppState {
  // App mode
  appMode: AppMode
  // preserveContext: keep the selected portfolio/project/context across the
  // switch (in-place lifecycle change). Callers must first verify the selection
  // survives the new mode's data scope — see ModeSwitcher.selectionSurvives.
  setAppMode: (mode: AppMode, opts?: { preserveContext?: boolean }) => void

  // Demo profile
  demoProfileId: string
  setDemoProfile: (profileId: string) => void

  // Demo company scope (#67-lite) — when set, the management context is scoped
  // to one company (see types/demoCompanies.ts). Null = unscoped (full directory).
  currentCompanyId: string | null
  setCurrentCompany: (companyId: string | null) => void

  // Display-currency override (null = follow selected company's native currency).
  // Drives the app-wide A$/US$ money toggle (see lib/utils currency context).
  displayCurrencyOverride: string | null
  setDisplayCurrencyOverride: (currency: string | null) => void

  // Sandbox organization scope — Sandbox's parallel to Demo's company scope.
  // When set (Sandbox mode only), portfolios/projects are narrowed to one
  // organization (by `organizationId`). Null = all organizations (org-wide,
  // the prior default). Lets a sandbox focus on e.g. Mach12, which has no
  // demo-profile tile on the homepage. See usePortfolioData.
  sandboxOrgId: string | null
  setSandboxOrg: (orgId: string | null) => void

  // Active organization (production edition) — the over-arching org scope that
  // frames every module. Null until resolved (login roster-bind or the top
  // Active-Org bar). Distinct from the demo `currentCompanyId` + `sandboxOrgId`
  // scopes; this is the real org id used in production.
  activeOrganizationId: string | null
  setActiveOrganization: (orgId: string | null) => void

  // User
  currentUser: CurrentUser
  setCurrentUser: (user: CurrentUser) => void
  /**
   * Bumped whenever the timesheet reference-data cache (employees/roster) is
   * (re)loaded from Supabase. Components that read the non-reactive roster
   * (PersonaSwitcher, Sidebar) subscribe to this so they re-render and
   * re-resolve the active persona once the real roster replaces the boot seed.
   */
  referenceDataVersion: number
  bumpReferenceDataVersion: () => void
  /**
   * Demo-only override for the timesheet access role (#1 persona switcher).
   * The deployed demo runs on the anon key, but `users` RLS only allows
   * `authenticated` to read `system_role` — so useCurrentAccessSubject can't
   * resolve a persona's real role and falls back to 'Supervisor'. When the
   * persona switcher sets this, useCurrentAccessSubject uses it directly,
   * making the switcher authoritative. Null outside the demo switcher.
   */
  timesheetRoleOverride: string | null
  setTimesheetRoleOverride: (role: string | null) => void

  // Navigation
  sidebarExpanded: boolean
  toggleSidebar: () => void
  setSidebarExpanded: (expanded: boolean) => void
  // Mobile only: the off-canvas nav drawer open/closed state (desktop ignores this).
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void

  // Management context
  managementContext: ManagementContext | null
  setManagementContext: (ctx: ManagementContext | null) => void

  // Portfolio context
  selectedPortfolio: Portfolio | null
  selectPortfolio: (portfolio: Portfolio | null) => void
  contextSelected: boolean

  // Project context
  selectedProject: Project | null
  selectProject: (project: Project | null) => void

  // AI Assistant
  aiPanelOpen: boolean
  toggleAiPanel: () => void
  openAiPanelWithPrompt: (prompt: string) => void
  pendingAiPrompt: string | null
  clearPendingAiPrompt: () => void
  aiMessages: AiMessage[]
  addAiMessage: (msg: AiMessage) => void
  clearAiMessages: () => void

  // UI
  density: 'compact' | 'comfortable'
  setDensity: (d: 'compact' | 'comfortable') => void

  // Agile Delivery — backlog / board / sprint view state (in-memory; resets on
  // reload). agileBoardId is the selected board; agileSprintFilter scopes the
  // board to a sprint; backlogFilters drive the Backlog page filter bar;
  // boardSwimlaneOverride overrides the board's swimlane grouping this session.
  agileBoardId: string | null
  setAgileBoardId: (id: string | null) => void
  agileSprintFilter: string | null
  setAgileSprintFilter: (id: string | null) => void
  backlogFilters: BacklogFilters
  setBacklogFilters: (f: BacklogFilters) => void
  resetBacklogFilters: () => void
  boardSwimlaneOverride: SwimlaneBy | null
  setBoardSwimlaneOverride: (s: SwimlaneBy | null) => void
  /** Whether the board shows stories or tasks as cards (persisted). */
  boardItemLevel: BoardItemLevel
  setBoardItemLevel: (level: BoardItemLevel) => void
  /** Saved backlog filter presets, keyed by project id (persisted). */
  backlogFilterPresets: Record<string, FilterPreset[]>
  saveBacklogFilterPreset: (projectId: string, preset: FilterPreset) => void
  deleteBacklogFilterPreset: (projectId: string, presetId: string) => void

  // Conversational Analytics — "Review plans before running" gate (BUILD_PLAN
  // 5.3). When true, a submitted plan renders for review behind an explicit Run
  // button instead of auto-running. Persisted to localStorage.
  caReviewPlans: boolean
  setCaReviewPlans: (v: boolean) => void

  // Timesheet — worker pay type drives which lane shows in the
  // sidebar pill switcher (salary timesheet grid vs hourly wand UX).
  timesheetPayType: TimesheetPayType
  /** True once the user manually flips the pill this session; auto-routing
   *  from the worker's payClass then stops overriding their choice. */
  timesheetPayTypeManual: boolean
  setTimesheetPayType: (payType: TimesheetPayType) => void
  /** Route the lane from the worker's payClass (#5). No-op once the user has
   *  manually chosen, or when already on that lane. Does not persist — a manual
   *  flip is the only thing that sticks across sessions. */
  autoRouteTimesheetPayType: (payType: TimesheetPayType) => void
}

export const useAppStore = create<AppState>((set) => ({
  appMode: loadPersistedMode(),
  setAppMode: (mode, opts) => {
    // A project-restricted user stays in their own account (no shared demo login).
    if (mode === 'demo' && !mayLeaveOwnAccount()) {
      toast.error('Demo profiles are not available on your account.')
      return
    }
    const preserveContext = opts?.preserveContext === true
    try { localStorage.setItem('ppm-app-mode', mode) } catch { /* ignore */ }
    clearMockCache()
    // Fully remove all cached query data so stale demo data doesn't persist
    queryClient.removeQueries()
    // Clear persisted context too — switching modes shouldn't carry the
    // previous mode's selected portfolio / project forward... UNLESS the
    // caller verified the selection survives the new mode's data scope
    // (in-place lifecycle switch, tunnel model: the page stays mounted and
    // re-renders with the new mode's data instead of bouncing home).
    if (!preserveContext) {
      try {
        localStorage.removeItem(PERSIST_KEY_PORTFOLIO)
        localStorage.removeItem(PERSIST_KEY_PROJECT)
        localStorage.removeItem(PERSIST_KEY_MGMT_CTX)
      } catch { /* ignore */ }
    }
    const contextReset = preserveContext
      ? {}
      : {
          selectedPortfolio: null,
          selectedProject: null,
          contextSelected: false,
          managementContext: null,
        }
    // When switching to demo, reset to demo user and set demo org
    if (mode === 'demo') {
      try { localStorage.setItem('ppm-organization-id', 'org_demo') } catch { /* ignore */ }
      set({
        appMode: mode,
        currentUser: { ...DEMO_USER },
        timesheetRoleOverride: null,
        ...contextReset,
      })
      // Entering demo (deliberately, via the Mode Switcher — after the login
      // wall) signs into the demo account so demo data + RLS resolve. The wall
      // still gates entry to the app, so this is never an anonymous bypass; it
      // is the one place a demo session is established now that boot no longer
      // auto-signs-in an anonymous visitor.
      void useAuthStore.getState().signInAsDemo(getDemoProfile(useAppStore.getState().demoProfileId))
    } else {
      // For non-demo modes, org ID comes from the authenticated user's metadata
      // (set by the auth bridge in App.tsx). Clear any stale demo org.
      try { localStorage.removeItem('ppm-organization-id') } catch { /* ignore */ }
      // Tunnel continuity guard: entering SANDBOX from a DEMO ride must not
      // silently swap the org to a stale earlier sandbox pick (riding Gentex,
      // switching to Sandbox flipped the workspace to Mach12 — a pick from a
      // previous sandbox visit, restored from memory). Demo companies can't BE
      // sandbox orgs (excluded from the picker), so the ridden identity stays
      // as the inherited label and the org picker opens for a real choice.
      // Mode rides WITHIN the real-org lane (sandbox ↔ dev/quality/production)
      // keep the picked org — that's the tunnel promise.
      const { appMode: prevMode, currentCompanyId } = useAppStore.getState()
      const clearStaleSandboxOrg = mode === 'sandbox' && prevMode === 'demo' && !!currentCompanyId
      set({
        appMode: mode,
        timesheetRoleOverride: null,
        ...contextReset,
        ...(clearStaleSandboxOrg ? { activeOrganizationId: null } : {}),
      })
    }
  },

  demoProfileId: loadPersistedDemoProfile(),
  setDemoProfile: (profileId) => {
    // Signing in to a shared demo account would step around a project restriction.
    if (!mayLeaveOwnAccount()) {
      toast.error('Demo profiles are not available on your account.')
      return
    }
    try { localStorage.setItem('ppm-demo-profile', profileId) } catch { /* ignore */ }
    clearMockCache()
    // No cache clear/invalidate here: clear() flashed the launchpad to
    // skeletons, and invalidating BEFORE the demo auth swap caused a double
    // refetch wave (pre-auth + post-auth). signInAsDemo() below owns the ONE
    // invalidate, after the session settles — stale data stays visible until
    // the new profile's data arrives. Same freshness, no flash.
    // Clear persisted context — different demo profile = different portfolios.
    try {
      localStorage.removeItem(PERSIST_KEY_PORTFOLIO)
      localStorage.removeItem(PERSIST_KEY_PROJECT)
      localStorage.removeItem(PERSIST_KEY_MGMT_CTX)
    } catch { /* ignore */ }
    const profile = getDemoProfile(profileId)
    set({
      demoProfileId: profileId,
      currentUser: { ...DEMO_USER, department: profile.industry },
      selectedPortfolio: null,
      selectedProject: null,
      contextSelected: false,
      managementContext: null,
    })
    // Auto-sign-in as the demo user for this profile (Supabase auth for RLS)
    useAuthStore.getState().signInAsDemo(profile)
  },

  currentCompanyId: loadPersistedCompany(),
  setCurrentCompany: (companyId) => {
    const prevCompanyId = useAppStore.getState().currentCompanyId
    try {
      if (companyId) localStorage.setItem('ppm-demo-company', companyId)
      else localStorage.removeItem('ppm-demo-company')
    } catch { /* ignore */ }
    set({ currentCompanyId: companyId })
    // A display-currency override is per-company intent, not a sticky global: on a
    // REAL company change, reset it so each company shows its OWN native currency
    // (e.g. Codan A$ → Gentex US$ — the override no longer bleeds across companies,
    // which was making Gentex render in A$). A same-company reconcile keeps any
    // active override so a user's deliberate in-company toggle survives churn.
    if (companyId !== prevCompanyId) {
      try { localStorage.removeItem('ppm-display-currency') } catch { /* ignore */ }
      set({ displayCurrencyOverride: null })
      syncCurrencyContext(companyId, null)
    } else {
      syncCurrencyContext(companyId, useAppStore.getState().displayCurrencyOverride)
    }
  },

  displayCurrencyOverride: loadPersistedDisplayCurrency(loadPersistedCompany()),
  setDisplayCurrencyOverride: (currency) => {
    const companyId = useAppStore.getState().currentCompanyId
    try {
      // Scope the persisted choice to its company so it never bleeds on a fresh
      // load onto a different company (see loadPersistedDisplayCurrency).
      if (currency) localStorage.setItem('ppm-display-currency', `${companyId ?? ''}:${currency}`)
      else localStorage.removeItem('ppm-display-currency')
    } catch { /* ignore */ }
    set({ displayCurrencyOverride: currency })
    syncCurrencyContext(companyId, currency)
    // Re-render every money-bearing view with the new display currency.
    queryClient.invalidateQueries()
  },

  sandboxOrgId: loadPersistedSandboxOrg(),
  setSandboxOrg: (orgId) => {
    try {
      if (orgId) localStorage.setItem('ppm-sandbox-org', orgId)
      else localStorage.removeItem('ppm-sandbox-org')
    } catch { /* ignore */ }
    set({ sandboxOrgId: orgId })
  },

  activeOrganizationId: loadPersistedActiveOrg(),
  setActiveOrganization: (orgId) => {
    try {
      if (orgId) localStorage.setItem('ppm-organization-id', orgId)
      else localStorage.removeItem('ppm-organization-id')
    } catch { /* ignore */ }
    set({ activeOrganizationId: orgId })
  },

  currentUser: loadPersistedMode() === 'demo'
    ? (() => {
        // Restore the last-picked persona's identity on refresh, immediately,
        // from the persisted snapshot — no wait on the async employee cache.
        const persona = loadPersistedPersona()
        return persona
          ? { ...DEMO_USER, id: persona.id, name: persona.name, initials: persona.initials, role: persona.appRole }
          : { ...DEMO_USER }
      })()
    : {
        id: 'user_001',
        name: 'John Smith',
        role: 'PortfolioManager',
        organizationId: null,
        organizationName: null,
        assignedPortfolios: ['port_001', 'port_002'],
        costCenterCode: '75-04-500',
        clearance: 'Secret',
        department: 'Engineering',
        initials: 'JS',
      },
  setCurrentUser: (user) => set({ currentUser: user }),
  referenceDataVersion: 0,
  bumpReferenceDataVersion: () => set((s) => ({ referenceDataVersion: s.referenceDataVersion + 1 })),
  timesheetRoleOverride: loadPersistedRoleOverride(),
  setTimesheetRoleOverride: (role) => {
    try {
      if (role) localStorage.setItem(PERSIST_KEY_TS_ROLE_OVERRIDE, role)
      else localStorage.removeItem(PERSIST_KEY_TS_ROLE_OVERRIDE)
    } catch {
      /* ignore */
    }
    set({ timesheetRoleOverride: role })
  },

  sidebarExpanded: true,
  toggleSidebar: () => set((s) => ({ sidebarExpanded: !s.sidebarExpanded })),
  setSidebarExpanded: (expanded) => set({ sidebarExpanded: expanded }),

  mobileNavOpen: false,
  setMobileNavOpen: (open) => set({ mobileNavOpen: open }),

  managementContext: loadPersistedManagementContext(),
  setManagementContext: (ctx) => {
    try {
      if (ctx) localStorage.setItem(PERSIST_KEY_MGMT_CTX, ctx)
      else localStorage.removeItem(PERSIST_KEY_MGMT_CTX)
    } catch { /* ignore */ }
    set({ managementContext: ctx })
  },

  // Hydrate from localStorage so a page refresh doesn't bounce the user
  // back to the Context Selector. Both portfolio and project objects are
  // stored as full JSON; if Supabase data has changed since the last
  // visit, the next user interaction will overwrite stale values.
  selectedPortfolio: loadPersistedPortfolio(),
  contextSelected: loadPersistedPortfolio() !== null,
  selectPortfolio: (portfolio) => {
    try {
      if (portfolio) localStorage.setItem(PERSIST_KEY_PORTFOLIO, JSON.stringify(portfolio))
      else localStorage.removeItem(PERSIST_KEY_PORTFOLIO)
    } catch { /* ignore */ }
    set({ selectedPortfolio: portfolio, contextSelected: portfolio !== null })
  },

  selectedProject: loadPersistedProject(),
  selectProject: (project) => {
    try {
      if (project) localStorage.setItem(PERSIST_KEY_PROJECT, JSON.stringify(project))
      else localStorage.removeItem(PERSIST_KEY_PROJECT)
    } catch { /* ignore */ }
    set({ selectedProject: project })
  },

  aiPanelOpen: false,
  toggleAiPanel: () => set((s) => ({ aiPanelOpen: !s.aiPanelOpen })),
  openAiPanelWithPrompt: (prompt) => set({ aiPanelOpen: true, pendingAiPrompt: prompt }),
  pendingAiPrompt: null,
  clearPendingAiPrompt: () => set({ pendingAiPrompt: null }),
  aiMessages: [],
  addAiMessage: (msg) => set((s) => ({ aiMessages: [...s.aiMessages, msg] })),
  clearAiMessages: () => set({ aiMessages: [] }),

  density: 'comfortable',
  setDensity: (d) => set({ density: d }),

  agileBoardId: null,
  setAgileBoardId: (id) => set({ agileBoardId: id }),
  agileSprintFilter: null,
  setAgileSprintFilter: (id) => set({ agileSprintFilter: id }),
  backlogFilters: DEFAULT_BACKLOG_FILTERS,
  setBacklogFilters: (f) => set({ backlogFilters: f }),
  resetBacklogFilters: () => set({ backlogFilters: DEFAULT_BACKLOG_FILTERS }),
  boardSwimlaneOverride: null,
  setBoardSwimlaneOverride: (s) => set({ boardSwimlaneOverride: s }),
  boardItemLevel: loadPersistedBoardItemLevel(),
  setBoardItemLevel: (level) => {
    try {
      localStorage.setItem(PERSIST_KEY_BOARD_ITEM_LEVEL, level)
    } catch {
      /* storage unavailable - the choice still holds for this session */
    }
    set({ boardItemLevel: level })
  },

  backlogFilterPresets: loadPersistedBacklogPresets(),
  saveBacklogFilterPreset: (projectId, preset) =>
    set((state) => {
      const existing = state.backlogFilterPresets[projectId] ?? []
      // Replace a same-named preset in place; otherwise append.
      const without = existing.filter((p) => p.name !== preset.name)
      const next = { ...state.backlogFilterPresets, [projectId]: [...without, preset] }
      persistBacklogPresets(next)
      return { backlogFilterPresets: next }
    }),
  deleteBacklogFilterPreset: (projectId, presetId) =>
    set((state) => {
      const existing = state.backlogFilterPresets[projectId] ?? []
      const next = { ...state.backlogFilterPresets, [projectId]: existing.filter((p) => p.id !== presetId) }
      persistBacklogPresets(next)
      return { backlogFilterPresets: next }
    }),

  caReviewPlans: loadPersistedCaReviewPlans(),
  setCaReviewPlans: (v) => {
    try {
      localStorage.setItem(PERSIST_KEY_CA_REVIEW_PLANS, v ? 'true' : 'false')
    } catch {
      /* ignore */
    }
    set({ caReviewPlans: v })
  },

  timesheetPayType: loadPersistedTimesheetPayType(),
  timesheetPayTypeManual: false,
  setTimesheetPayType: (payType) => {
    try {
      localStorage.setItem(PERSIST_KEY_TS_PAY_TYPE, payType)
    } catch {
      /* ignore */
    }
    set({ timesheetPayType: payType, timesheetPayTypeManual: true })
  },
  autoRouteTimesheetPayType: (payType) => {
    set((state) =>
      state.timesheetPayTypeManual || state.timesheetPayType === payType
        ? {}
        : { timesheetPayType: payType },
    )
  },
}))

// On load, point the money formatters at the persisted company's native currency
// (+ any persisted display override that belongs to THIS company) so figures
// render correctly before the user touches anything.
{
  const persistedCompany = loadPersistedCompany()
  syncCurrencyContext(persistedCompany, loadPersistedDisplayCurrency(persistedCompany))
}
